import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSession } from "@/services/auth0/session";
import { useEffect, useMemo, useRef } from "react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import { PhoneInputShadcn } from "@/components/ui/phone-input-shadcn";
import {
  Card,
  CardContent,
  CardFooter,
  CardTitle,
  CardHeader,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, PlusCircle, Trash2, AlertCircle } from "lucide-react";
import { UpdateOrderSchema, updateOrderSchema } from "@/api.schemas";
import { useOrderById } from "@/api/use-order";
import { usePricingOptions } from "@/api/use-order-pricing-options";
import { useUpdateOrder } from "@/api/use-order";
import { useUpdateParcels } from "@/api/use-parcel";
import { ShipmentMap } from "./shipment-map";
import { Order, OrderStatus } from "@/api.types";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface EditOrderFormProps {
  orderId: string;
  onSuccess: () => void;
}

function canEditParcels(status: Order["status"]) {
  return [
    OrderStatus.REGISTERED,
    OrderStatus.PICKUP_PENDING,
    OrderStatus.OUT_FOR_PICKUP,
  ].includes(status as OrderStatus);
}

function canEditAddresses(status: Order["status"]) {
  return [
    OrderStatus.REGISTERED,
    OrderStatus.PICKUP_PENDING,
    OrderStatus.OUT_FOR_PICKUP,
    OrderStatus.DELIVERY_PENDING,
    OrderStatus.OUT_FOR_DELIVERY,
  ].includes(status as OrderStatus);
}

function canEditContactInfo(status: Order["status"]) {
  return [
    OrderStatus.REGISTERED,
    OrderStatus.PICKUP_PENDING,
    OrderStatus.PICKED_UP,
    OrderStatus.OUT_FOR_PICKUP,
    OrderStatus.DELIVERY_PENDING,
    OrderStatus.OUT_FOR_DELIVERY,
  ].includes(status as OrderStatus);
}

export function EditOrderForm({ orderId, onSuccess }: EditOrderFormProps) {
  const { customer } = useSession();
  const form = useForm<UpdateOrderSchema>({
    resolver: zodResolver(updateOrderSchema),
    defaultValues: {
      senderName: "",
      senderPhone: "",
      pickupAddress: undefined,
      recipientName: "",
      recipientPhone: "",
      deliveryAddress: undefined,
      pickupNotes: "",
      deliveryNotes: "",
      type: "STANDARD",
      parcels: [
        {
          weight: 2,
          dimensions: { length: 30, width: 30, height: 30 },
          isFragile: false,
          description: "",
        },
      ],
    },
  });

  // Store original values for change detection
  const originalValues = useRef<UpdateOrderSchema | null>(null);

  const watchedValues = useWatch({
    control: form.control,
    name: ["pickupAddress", "deliveryAddress", "parcels"],
  });

  const pricingInputs = useMemo(
    () => ({
      pickupAddress: watchedValues[0],
      deliveryAddress: watchedValues[1],
      parcels: watchedValues[2]?.map((parcel) => ({
        weight: Number(parcel.weight) || 0,
        dimensions: {
          length: Number(parcel.dimensions?.length) || 0,
          width: Number(parcel.dimensions?.width) || 0,
          height: Number(parcel.dimensions?.height) || 0,
        },
        isFragile: Boolean(parcel.isFragile),
      })),
    }),
    [watchedValues]
  );

  const {
    pricingOptions,
    pricingOptionMetadata,
    error: shippingOptionsError,
  } = usePricingOptions(pricingInputs);

  // Fetch order details
  const { data: order } = useOrderById(orderId);

  // Update form when order data is loaded
  useEffect(() => {
    if (order) {
      // Default empty address structure that satisfies the type requirements
      const emptyAddress = {
        line1: "",
        line2: "",
        city: "",
        province: "",
        postalCode: "",
        country: "",
        location: { lat: 0, lng: 0 },
      };

      // Create a properly typed initialValues object that matches UpdateOrderSchema
      const initialValues = {
        senderName: order.senderName ?? "",
        senderPhone: order.senderPhone ?? "",
        pickupAddress: order.pickupAddress
          ? {
              line1: order.pickupAddress.line1 ?? "",
              line2: order.pickupAddress.line2,
              city: order.pickupAddress.city ?? "",
              province: order.pickupAddress.province ?? "",
              postalCode: order.pickupAddress.postalCode ?? "",
              country: order.pickupAddress.country ?? "",
              location: order.pickupAddress.location ?? { lat: 0, lng: 0 },
            }
          : emptyAddress,
        recipientName: order.recipientName ?? "",
        recipientPhone: order.recipientPhone ?? "",
        deliveryAddress: order.deliveryAddress
          ? {
              line1: order.deliveryAddress.line1 ?? "",
              line2: order.deliveryAddress.line2,
              city: order.deliveryAddress.city ?? "",
              province: order.deliveryAddress.province ?? "",
              postalCode: order.deliveryAddress.postalCode ?? "",
              country: order.deliveryAddress.country ?? "",
              location: order.deliveryAddress.location ?? { lat: 0, lng: 0 },
            }
          : emptyAddress,
        type: order.type as "STANDARD" | "EXPRESS" | "SAME_DAY",
        parcels: order.parcels.map((parcel) => ({
          weight: parcel.weight,
          dimensions: {
            length: parcel.dimensions.length,
            width: parcel.dimensions.width,
            height: parcel.dimensions.height,
          },
          isFragile: parcel.isFragile,
          description: parcel.description ?? "",
        })),
        pickupNotes: order.pickupNotes,
        deliveryNotes: order.deliveryNotes,
      };

      form.reset(initialValues);
      originalValues.current = initialValues;
    }
  }, [order, form]);

  // Add this useEffect to handle initial order type
  useEffect(() => {
    if (order && pricingOptions.length > 0) {
      // Ensure the order type is set in the form
      form.setValue("type", order.type as "STANDARD" | "EXPRESS" | "SAME_DAY");
    }
  }, [order, pricingOptions, form]);

  // Replace the existing mutations with the new hooks
  const updateOrderMutation = useUpdateOrder(orderId, { onSuccess });
  const updateParcelsMutation = useUpdateParcels(orderId, { onSuccess });

  const onSubmit = form.handleSubmit(async (values) => {
    // Update order details
    await updateOrderMutation.mutateAsync({
      ...values,
      senderCustomerId: customer?.id,
      pickupDepotId: pricingOptionMetadata?.pickupServiceArea?.depotId,
      deliveryDepotId: pricingOptionMetadata?.deliveryServiceArea?.depotId,
    });

    // Update parcels - no need to pass current order anymore
    await updateParcelsMutation.mutateAsync(values.parcels);
  });

  const isParcelEditable = order ? canEditParcels(order.status) : false;
  const isAddressEditable = order ? canEditAddresses(order.status) : false;
  const isContactEditable = order ? canEditContactInfo(order.status) : false;

  // Check if anything is editable
  const isAnythingEditable =
    isParcelEditable || isAddressEditable || isContactEditable;

  // Check if addresses have been changed
  const currentValues = form.getValues();
  const hasPickupAddressChanged =
    originalValues.current &&
    JSON.stringify(originalValues.current.pickupAddress) !==
      JSON.stringify(currentValues.pickupAddress);

  const hasDeliveryAddressChanged =
    originalValues.current &&
    JSON.stringify(originalValues.current.deliveryAddress) !==
      JSON.stringify(currentValues.deliveryAddress);

  const haveParcelsChanged =
    originalValues.current &&
    JSON.stringify(originalValues.current.parcels) !==
      JSON.stringify(currentValues.parcels);

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-1 flex-col">
        <div className="flex-1 space-y-8 flex-col flex mx-auto w-full lg:max-w-7xl px-4 lg:px-0">
          {/* Sender and Recipient Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Sender Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-left">Sender Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="senderName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sender Name</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={!isContactEditable} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="senderPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sender Phone</FormLabel>
                      <FormControl>
                        <PhoneInputShadcn
                          {...field}
                          value={field.value}
                          onChange={(value) => field.onChange(value ?? "")}
                          defaultCountry="CA"
                          disabled={!isContactEditable}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="pickupAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pickup Address</FormLabel>
                      <FormControl>
                        <AutocompleteAddress
                          onSelectedAddress={(address) => {
                            if (address && isAddressEditable) {
                              // For line2 changes, preserve existing location
                              const existingLocation = field.value?.location;
                              form.setValue(
                                "pickupAddress",
                                {
                                  line1: address.addressComponents.line1,
                                  line2: address.addressComponents.line2,
                                  city: address.addressComponents.city,
                                  province: address.addressComponents.state,
                                  postalCode:
                                    address.addressComponents.postalCode,
                                  country: address.addressComponents.country,
                                  location:
                                    address.location.lat === 0
                                      ? existingLocation
                                      : address.location,
                                },
                                { shouldValidate: address.location.lat !== 0 }
                              ); // Only validate if it's not a line2 change
                            }
                          }}
                          initialValue={field.value?.line1}
                          initialAddressComponents={{
                            line1: field.value?.line1 ?? "",
                            line2: field.value?.line2 ?? "",
                            city: field.value?.city ?? "",
                            state: field.value?.province ?? "",
                            postalCode: field.value?.postalCode ?? "",
                            country: field.value?.country ?? "",
                          }}
                          showEditFormByDefault={true}
                          readOnly={!isAddressEditable}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="pickupNotes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pickup Notes</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Add any special instructions for pickup"
                          disabled={!isContactEditable}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Recipient Card */}
            <Card>
              <CardHeader>
                <CardTitle>Recipient Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="recipientName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Recipient Name</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={!isContactEditable} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="recipientPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Recipient Phone</FormLabel>
                      <FormControl>
                        <PhoneInputShadcn
                          {...field}
                          value={field.value}
                          onChange={(value) => field.onChange(value ?? "")}
                          defaultCountry="CA"
                          disabled={!isContactEditable}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="deliveryAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Delivery Address</FormLabel>
                      <FormControl>
                        <AutocompleteAddress
                          onSelectedAddress={(address) => {
                            if (address && isAddressEditable) {
                              // For line2 changes, preserve existing location
                              const existingLocation = field.value?.location;
                              form.setValue(
                                "deliveryAddress",
                                {
                                  line1: address.addressComponents.line1,
                                  line2: address.addressComponents.line2,
                                  city: address.addressComponents.city,
                                  province: address.addressComponents.state,
                                  postalCode:
                                    address.addressComponents.postalCode,
                                  country: address.addressComponents.country,
                                  location:
                                    address.location.lat === 0
                                      ? existingLocation
                                      : address.location,
                                },
                                { shouldValidate: address.location.lat !== 0 }
                              ); // Only validate if it's not a line2 change
                            }
                          }}
                          initialValue={field.value?.line1}
                          initialAddressComponents={{
                            line1: field.value?.line1 ?? "",
                            line2: field.value?.line2 ?? "",
                            city: field.value?.city ?? "",
                            state: field.value?.province ?? "",
                            postalCode: field.value?.postalCode ?? "",
                            country: field.value?.country ?? "",
                          }}
                          showEditFormByDefault={true}
                          readOnly={!isAddressEditable}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="deliveryNotes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Delivery Notes</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Add any special instructions for delivery"
                          disabled={!isContactEditable}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </div>

          {/* Parcels Card */}
          <Card>
            <CardHeader>
              <CardTitle>Parcels</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dimensions (L×W×H cm)</TableHead>
                    <TableHead>Weight (kg)</TableHead>
                    <TableHead>Special Handling</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {form.watch("parcels")?.map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Input
                            type="number"
                            placeholder="L"
                            {...form.register(
                              `parcels.${index}.dimensions.length`,
                              {
                                valueAsNumber: true,
                              }
                            )}
                            className="w-20"
                            disabled={!isParcelEditable}
                          />
                          <Input
                            type="number"
                            placeholder="W"
                            {...form.register(
                              `parcels.${index}.dimensions.width`,
                              {
                                valueAsNumber: true,
                              }
                            )}
                            className="w-20"
                            disabled={!isParcelEditable}
                          />
                          <Input
                            type="number"
                            placeholder="H"
                            {...form.register(
                              `parcels.${index}.dimensions.height`,
                              {
                                valueAsNumber: true,
                              }
                            )}
                            className="w-20"
                            disabled={!isParcelEditable}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          placeholder="Weight"
                          {...form.register(`parcels.${index}.weight`)}
                          className="w-24"
                          disabled={!isParcelEditable}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Checkbox
                            checked={form.watch(`parcels.${index}.isFragile`)}
                            onCheckedChange={(checked) =>
                              isParcelEditable &&
                              form.setValue(
                                `parcels.${index}.isFragile`,
                                !!checked
                              )
                            }
                            disabled={!isParcelEditable}
                          />
                          <span className="text-sm">Fragile</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {isParcelEditable &&
                          form.watch("parcels")?.length > 1 && (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() => {
                                const parcels = form.getValues("parcels");
                                form.setValue(
                                  "parcels",
                                  parcels.filter((_, i) => i !== index)
                                );
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
            <CardFooter className="justify-center border-t p-4">
              {isParcelEditable && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="gap-1"
                  onClick={() => {
                    const parcels = form.getValues("parcels") || [];
                    form.setValue("parcels", [
                      ...parcels,
                      {
                        weight: 2,
                        dimensions: { length: 30, width: 30, height: 30 },
                        isFragile: false,
                        description: "",
                      },
                    ]);
                  }}
                >
                  <PlusCircle className="h-3.5 w-3.5" />
                  Add Parcel
                </Button>
              )}
            </CardFooter>
          </Card>

          {/* Map and Pricing Options Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pb-8">
            {/* Map Card */}
            <Card className="h-[600px]">
              <CardContent className="p-0 h-full">
                {!!form.watch("pickupAddress.location")?.lat &&
                !!form.watch("deliveryAddress.location")?.lat ? (
                  <ShipmentMap
                    pickupLocation={form.watch("pickupAddress.location")}
                    deliveryLocation={form.watch("deliveryAddress.location")}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <p>Enter pickup and delivery addresses</p>
                      <p className="text-sm">to view the shipment route</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pricing Options Card */}
            <Card>
              <CardHeader>
                <CardTitle>Delivery Options</CardTitle>
              </CardHeader>
              <CardContent>
                {pricingOptions.length > 0 ? (
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <div className="space-y-4">
                        {pricingOptions.map((option) => (
                          <div
                            key={option.speedType}
                            className={`p-6 rounded-xl max-w-[500px] border-2 ${
                              isAddressEditable || isParcelEditable
                                ? "cursor-pointer transition-all"
                                : "cursor-not-allowed"
                            } ${
                              field.value === option.speedType
                                ? "border-blue-500 bg-blue-50/50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                            onClick={() => {
                              if (isAddressEditable || isParcelEditable) {
                                field.onChange(
                                  option.speedType as
                                    | "STANDARD"
                                    | "EXPRESS"
                                    | "SAME_DAY"
                                );
                              }
                            }}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-medium text-lg">
                                  {option.speedType.replace("_", " ")}
                                </h4>
                                <p className="text-sm text-gray-500 mt-1">
                                  {option.speedDescription}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-medium">
                                  ${option.price.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  />
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500">
                      {shippingOptionsError ?? (
                        <>
                          {form.watch("pickupAddress") &&
                          form.watch("deliveryAddress")
                            ? "Calculating delivery options..."
                            : "Complete pickup and delivery addresses to view options"}
                        </>
                      )}
                    </p>
                    {shippingOptionsError && (
                      <p className="text-sm text-amber-600 mt-2">
                        {shippingOptionsError}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="border-t bg-background py-4">
          <div className="flex flex-col gap-4 lg:max-w-7xl mx-auto">
            {/* Show alerts when fields have changed */}
            <div className="space-y-3">
              {isAddressEditable && hasPickupAddressChanged && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Pickup Address Change Impact</AlertTitle>
                  <AlertDescription>
                    Changing the pickup address may affect delivery times and
                    could result in delays.
                  </AlertDescription>
                </Alert>
              )}

              {isAddressEditable && hasDeliveryAddressChanged && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Delivery Address Change Impact</AlertTitle>
                  <AlertDescription>
                    Changing the delivery address may affect delivery times and
                    could result in delays.
                  </AlertDescription>
                </Alert>
              )}

              {isParcelEditable && haveParcelsChanged && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Parcel Changes Impact</AlertTitle>
                  <AlertDescription>
                    Changing parcel size or count will affect the shipping
                    price.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div className="flex justify-end gap-4">
              {isAnythingEditable ? (
                <>
                  <Button type="button" variant="outline" onClick={onSuccess}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      updateOrderMutation.isPending ||
                      updateParcelsMutation.isPending
                    }
                  >
                    {updateOrderMutation.isPending ||
                    updateParcelsMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </>
              ) : (
                <Button type="button" onClick={onSuccess}>
                  Back
                </Button>
              )}
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}
